import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { notificationManager } from '@/Containers/CommunicationSection/NotificationContainer/Stores/NotificationManager'
import { offlineSyncManager } from '@/Ship/Pwa/OfflineSync'
import { cx } from '@/Ship/Helpers/cssClass'

import { AssignmentDetailDrawer } from '../Components/AssignmentDetailDrawer'
import { EmptyTasksState } from '../Components/EmptyTasks/EmptyTasksState'
import { MyShiftCard } from '../Components/MyShift/MyShiftCard'
import { TaskCard } from '../Components/TaskCard/TaskCard'
import { sortCurrentTasks, sortNextTasks } from '../Components/TaskCard/taskOrder'
import { IN_PROGRESS_STATUSES } from '../Components/TaskCard/taskStatus'
import { driverDashboardManager as store } from '../Stores/DriverDashboardManager'
import { myShiftStore as shiftStore, shiftCovers } from '../Stores/MyShiftStore'

import styles from './DashboardPage.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

export const pageConfig: IPageConfig = {
    route: '/driver',
    key: 'Driver.Dashboard',
    title: 'Кабінет водія',
    template: 'driver',
}

/**
 * Головна кабінету водія: рядок зміни, завдання з розпочатим виконанням («Зараз») і черга («Далі»).
 * Список приходить уже відфільтрованим за активною зміною (бекенд), тут лише порядок.
 */
const DashboardPage = observer(() => {
    document.title = 'Кабінет водія'
    const navigate = useNavigate()

    // «Зараз» — завдання з розпочатим виконанням, вищий статус вище;
    // «Далі» — ще не розпочаті, за датою й часом планового завантаження
    const current = sortCurrentTasks(store.assignments.filter((a) => IN_PROGRESS_STATUSES.includes(a.status)))
    const next = sortNextTasks(store.assignments.filter((a) => !IN_PROGRESS_STATUSES.includes(a.status)))
    const inProgressCount = current.filter((a) => shiftCovers(shiftStore.active, a)).length

    useEffect(() => {
        store.fetchAssignments()
        shiftStore.fetch()
    }, [])

    // Тап по push-сповіщенню про завдання: /driver?assignment=<uuid> — відкриваємо його картку,
    // а параметр прибираємо, щоб «Назад» чи перезавантаження не відкривали її знову
    const [searchParams, setSearchParams] = useSearchParams()
    const assignmentParam = searchParams.get('assignment')
    useEffect(() => {
        if (!assignmentParam) return
        store.requestOpenByUuid(assignmentParam)
        setSearchParams({}, { replace: true })
    }, [assignmentParam, setSearchParams])

    // Список завдань фільтрується за активною зміною: відкрили чи завершили зміну — перечитуємо
    useEffect(
        () =>
            reaction(
                () => shiftStore.active?.uuid ?? null,
                () => {
                    store.fetchAssignments()
                }
            ),
        []
    )

    useEffect(() => {
        // Якщо у черзі немає мутацій — оновлюємо список напряму.
        // Якщо є — useSyncOnReconnect запустить sync(), а DriverDashboardManager
        // підписаний на onSyncFinished і сам перезавантажить assignments.
        const handleOnline = () => {
            if (offlineSyncManager.pendingCount === 0) {
                store.fetchAssignments()
            }
            shiftStore.fetch()
        }
        window.addEventListener('online', handleOnline)
        return () => window.removeEventListener('online', handleOnline)
    }, [])

    return (
        <>
            <div className="pwa-page">
                <MyShiftCard store={shiftStore} inProgressCount={inProgressCount} />

                {store.cachedAt !== null && !store.loading && (
                    <div className={cx(styles, 'cacheNote')}>
                        Дані з кешу від{' '}
                        {new Date(store.cachedAt).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                )}

                {store.loading && (
                    <div className={cx(styles, 'skeleton')} aria-busy="true" aria-label="Завантаження завдань">
                        <div className={cx(styles, 'skeletonCard')} />
                        <div className={cx(styles, 'skeletonCard')} />
                    </div>
                )}

                {!store.loading && store.assignments.length === 0 && <EmptyTasksState />}

                {!store.loading && current.length > 0 && (
                    <>
                        <div className="pwa-eyebrow">Зараз {current.length > 1 && <b>· {current.length}</b>}</div>
                        {current.map((a) => (
                            <TaskCard key={a.uuid} assignment={a} variant="current" />
                        ))}
                    </>
                )}

                {!store.loading && next.length > 0 && (
                    <>
                        <div className="pwa-eyebrow">
                            {current.length > 0 ? 'Далі' : 'Завдання'} <b>· {next.length}</b>
                        </div>
                        {next.map((a) => (
                            <TaskCard key={a.uuid} assignment={a} />
                        ))}
                    </>
                )}
            </div>

            <AssignmentDetailDrawer
                assignment={store.selectedAssignment}
                open={store.drawerOpen}
                onClose={() => store.closeDrawer()}
                onStatusChanged={(uuid, status, shift) => store.updateAssignmentStatus(uuid, status, shift)}
                onViewNotifications={() => {
                    // Кабінет водія показує сповіщення окремою сторінкою (/driver/notifications),
                    // а не drawer'ом. Скроупимо список на відкрите завдання через entityFilter:
                    // всі сповіщення завдання несуть entityCode='Assignment' + entityUuid.
                    const uuid = store.selectedAssignment?.uuid
                    if (uuid) {
                        notificationManager.setEntityFilter({ entityCode: 'Assignment', entityUuid: uuid })
                    } else {
                        notificationManager.setEntityFilter(null)
                    }
                    store.closeDrawer()
                    navigate('/driver/notifications')
                }}
            />
        </>
    )
})

export default DashboardPage
